// rug-finder.js — PUBLIC Rug Finder island (v1, Option-A). A guided visual quiz
// builds ONE brief and runs the live matcher (window.ArteverkMatch) IN-PAGE on the
// public CDN feed, returning a RETAIL-priced curated edit rendered as the site's
// real product cards (.card / .cards — global theme.css styles it verbatim).
// No login, no server, no OPS endpoint. Soft capture via the native contact form
// (→ inbox/Klaviyo) + GA4 self-learning (rf_* events). This is the retail twin of
// trade-concierge.js — but RETAIL price only (never tradePriceCents) and no board.
(function () {
  const root = document.querySelector('[data-rf]');
  if (!root) return;
  const stage = root.querySelector('[data-rf-stage]');
  const stepsTpl = root.querySelector('[data-rf-steps]');
  const navTpl = root.querySelector('[data-rf-nav]');
  if (!stage || !stepsTpl || !navTpl) return;
  const M = window.ArteverkMatch; // matchRugs, formBriefToDesignBrief
  let feedPromise = null;

  // Pointer-aware, non-blocking feed load. Resolves to an array of rug rows.
  function loadFeed() {
    if (!feedPromise) {
      feedPromise = fetch(root.dataset.feed)
        .then((r) => r.json())
        .then((d) => {
          if (Array.isArray(d.rugs)) return d.rugs;
          if (d.url) return fetch(d.url).then((r) => r.json()).then((f) => f.rugs || []);
          return [];
        })
        .catch(() => []);
    }
    return feedPromise;
  }
  function track(event, params) {
    const p = Object.assign({ method: 'rug_finder' }, params || {});
    if (window.gtag) window.gtag('event', event, p);
    // The Finder fires a LOT of events (rf_step_view, rf_refine…); the helper filters to
    // conversion-grade names, so this stays a single call rather than a whitelist here.
    try { if (window.avPublishConversion) window.avPublishConversion(event, p); } catch (_) {}
  }

  // ── Resume-last-edit: persist the built brief so a returning visitor can pick up
  // where they left off. We store ONLY the answers object (+ a ts for ordering) under
  // a single key; restoring re-populates the live controls then jumps to results.
  const RESUME_KEY = 'rf_last_brief';
  function saveBrief() {
    try { localStorage.setItem(RESUME_KEY, JSON.stringify({ answers, ts: Date.now() })); } catch (_) {}
  }
  function readBrief() {
    try {
      const raw = JSON.parse(localStorage.getItem(RESUME_KEY));
      const a = raw && raw.answers ? raw.answers : null;
      return a && Object.keys(a).length ? a : null; // only a non-empty brief counts
    } catch (_) { return null; }
  }
  function clearBrief() { try { localStorage.removeItem(RESUME_KEY); } catch (_) {} }

  // ── Small render helpers (mirror trade-concierge.js) ───────────────────────
  const money = (c) => (c == null ? '' : '$' + (c / 100).toLocaleString('en-US', { maximumFractionDigits: 0 }));
  const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const imgAt = (url, w) => (url ? url + (url.includes('?') ? '&' : '?') + 'width=' + w : '');
  const wrapTo = (px) => { const w = root.querySelector('.rf-wrap'); if (w) w.style.maxWidth = px; };

  // ── Step model ─────────────────────────────────────────────────────────────
  const steps = [...stepsTpl.content.querySelectorAll('.rf-step')].map((n) => n.cloneNode(true));
  const STEP_KEYS = steps.map((s) => s.dataset.step);
  let i = 0;
  // { room, styles:[], colors:[], material, use, budgetMax, condition, ooakOnly,
  //   shape, widthMinFt/In, widthMaxFt/In, lengthMinFt/In, lengthMaxFt/In }
  // The size step's ft+in RANGE is the source of truth; quick-size chips just fill it.
  const answers = {};

  // Standard sizes → range-fill recipe (mirrors trade-concierge.js applySize).
  // Rectangles: width/length each fill a 1-foot band (W'0"–W'11", L'0"–L'11") so any
  // rug in that foot fits. Runner: shape=Runner, width 2'–3'11", length open. Round:
  // shape=Round, ranges open. Oversize: a 12'×15' floor, no upper bound.
  const RANGE_FIELDS = ['widthMinFt', 'widthMinIn', 'widthMaxFt', 'widthMaxIn', 'lengthMinFt', 'lengthMinIn', 'lengthMaxFt', 'lengthMaxIn'];
  const setRange = (step, f, v) => { const el = step.querySelector(`[data-rf="${f}"]`); if (el) el.value = (v == null ? '' : v); };
  const clearRanges = (step) => RANGE_FIELDS.forEach((f) => setRange(step, f, ''));
  function applySize(step, size) {
    clearRanges(step);
    delete answers.shape;
    if (size === 'runner') {
      answers.shape = 'Runner';
      setRange(step, 'widthMinFt', 2); setRange(step, 'widthMinIn', 0);
      setRange(step, 'widthMaxFt', 3); setRange(step, 'widthMaxIn', 11);
      return; // length left open
    }
    if (size === 'round') { answers.shape = 'Round'; return; } // ranges open
    if (size === 'oversize') {
      // Palace / oversize: open-ended large. Floor at 12'0" × 15'0", both maxes left empty.
      setRange(step, 'widthMinFt', 12); setRange(step, 'widthMinIn', 0);
      setRange(step, 'lengthMinFt', 15); setRange(step, 'lengthMinIn', 0);
      return;
    }
    const m = /^(\d+)x(\d+)$/.exec(size);
    if (!m) return;
    const w = Number(m[1]), l = Number(m[2]);
    setRange(step, 'widthMinFt', w); setRange(step, 'widthMinIn', 0);
    setRange(step, 'widthMaxFt', w); setRange(step, 'widthMaxIn', 11);
    setRange(step, 'lengthMinFt', l); setRange(step, 'lengthMinIn', 0);
    setRange(step, 'lengthMaxFt', l); setRange(step, 'lengthMaxIn', 11);
  }

  // ── Room -> a starting size ──────────────────────────────────────────────────
  // ⛔ AUDIT 2026-08-18: `answers.room` reached the matcher NOWHERE. It was read in
  // exactly one place — a sentence in the results copy — so the most prominent
  // question in the funnel (and the one the welcome popup now feeds) changed nothing
  // about which rugs came back. A room's real consequence is SIZE, which is the
  // matcher's one hard filter, so that is the honest connection.
  //
  // ⭐ These bands are NOT my opinion: they are our own published guidance in
  // docs/dtc/content/rug-size-guide.md — "front legs on… usually 8' x 10'" for a
  // living room, "size it so the rug extends about 24 inches beyond every edge of the
  // table" for dining, a Runner for halls. Bands are deliberately generous because
  // size is a HARD filter with a 12" tolerance; a tight guess would exclude good rugs.
  //
  // Applied ONLY when the visitor has not touched the size step, and applied to the
  // step they are about to SEE — so it is a visible starting point they can change,
  // never a hidden narrowing.
  const ROOM_SIZE = {
    'Living room':      { w: [8, 10], l: [10, 14] },
    'Dining room':      { w: [8, 10], l: [10, 14] },
    'Bedroom':          { w: [8, 10], l: [10, 14] },
    'Office':           { w: [5, 9],  l: [7, 12] },
    'Hallway / Runner': { runner: true },
  };
  function sizeStepIsUntouched(step) {
    if (answers.shape) return false;
    return !RANGE_FIELDS.some((f) => {
      const el = step.querySelector(`[data-rf="${f}"]`);
      return el && el.value !== '';
    });
  }
  function suggestSizeFromRoom() {
    const rec = ROOM_SIZE[answers.room];
    const step = steps[STEP_KEYS.indexOf('size')];
    if (!rec || !step || !sizeStepIsUntouched(step)) return;
    if (rec.runner) {
      applySize(step, 'runner');
    } else {
      setRange(step, 'widthMinFt', rec.w[0]);  setRange(step, 'widthMinIn', 0);
      setRange(step, 'widthMaxFt', rec.w[1]);  setRange(step, 'widthMaxIn', 11);
      setRange(step, 'lengthMinFt', rec.l[0]); setRange(step, 'lengthMinIn', 0);
      setRange(step, 'lengthMaxFt', rec.l[1]); setRange(step, 'lengthMaxIn', 11);
    }
    // Say where the numbers came from — a field that fills itself without explanation
    // reads as a bug, and the visitor must know it is theirs to change.
    let note = step.querySelector('[data-rf-sizenote]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'rf-hint';
      note.setAttribute('data-rf-sizenote', '');
      const chips = step.querySelector('.rf-chips') || step.firstElementChild;
      if (chips && chips.parentNode) chips.parentNode.insertBefore(note, chips.nextSibling);
    }
    note.textContent = 'A usual starting point for a ' + String(answers.room).toLowerCase()
      + ' — change anything below.';
    track('rf_size_from_room', { room: answers.room });
  }

  // Optional steps are skippable (room + size are the core); the rest can advance with
  // no selection via a quiet "Skip" link + an "(optional)" cue on the eyebrow.
  // 'character' merged into 'budget' 2026-08-17 — one closing step, not two thin ones.
  const OPTIONAL_STEPS = new Set(['style', 'palette', 'feel', 'budget']);

  loadFeed(); // warm the feed in the background

  function renderStep() {
    stage.innerHTML = '';
    const step = steps[i];
    // The step nodes are PERSISTENT (they hold the user's selections across Back/Next),
    // so a nav appended on a prior visit stays inside the node. Remove it before
    // re-appending — otherwise navs accumulate every time you return to a step
    // (the back-duplication bug). One nav per step, always.
    const existingNav = step.querySelector('.rf-nav');
    if (existingNav) existingNav.remove();
    stage.appendChild(step);
    // Append the nav INSIDE the step card so Back / progress / Next read as the
    // card's footer (the .rf-nav border-top makes it part of the card unit), not a
    // detached floating row. Click handlers are delegated on `stage`, and the step
    // is a child of `stage`, so the events still reach them unchanged.
    const nav = navTpl.content.firstElementChild.cloneNode(true);
    step.appendChild(nav);
    nav.querySelector('[data-rf-progress]').textContent = `Step ${i + 1} of ${steps.length}`;
    nav.querySelector('[data-rf-back]').style.visibility = i === 0 ? 'hidden' : 'visible';
    nav.querySelector('[data-rf-next]').textContent = i === steps.length - 1 ? 'See my edit' : 'Next';
    // On optional steps: a quiet "Skip" link beside Next + an "(optional)" eyebrow cue.
    if (OPTIONAL_STEPS.has(STEP_KEYS[i])) {
      const next = nav.querySelector('[data-rf-next]');
      const skip = document.createElement('button');
      skip.type = 'button';
      skip.className = 'rf-skip';
      skip.setAttribute('data-rf-skip', '');
      skip.textContent = 'Skip';
      next.insertAdjacentElement('beforebegin', skip);
      const eyebrow = step.querySelector('.rf-keyebrow');
      if (eyebrow && !eyebrow.querySelector('.rf-optional')) {
        const cue = document.createElement('span');
        cue.className = 'rf-optional';
        cue.textContent = ' (optional)';
        eyebrow.appendChild(cue);
      }
    }
    track('rf_step_view', { step: STEP_KEYS[i], index: i });
  }

  // Read the data-attr value off every pressed control inside a scope.
  function pressedData(scope, sel, dataKey) {
    return [...scope.querySelectorAll(`${sel}[aria-pressed="true"]`)].map((n) => n.dataset[dataKey]).filter(Boolean);
  }
  function collectStep() {
    const step = steps[i];
    const key = step.dataset.step;
    if (key === 'room') {
      answers.room = pressedData(step, '.rf-chip', 'room')[0];
      suggestSizeFromRoom();
    }
    if (key === 'size') {
      // The ft+in range is the source of truth — read it straight into answers (a typed
      // custom size works with no chip). answers.shape is set by runner/round chips.
      RANGE_FIELDS.forEach((f) => {
        const v = step.querySelector(`[data-rf="${f}"]`)?.value;
        if (v) answers[f] = Number(v); else delete answers[f];
      });
    }
    if (key === 'style') {
      answers.styles = pressedData(step, '.rf-chip', 'style');
      answers.pattern = pressedData(step, '[data-rf="pattern"] .rf-chip', 'pattern')[0];
      answers.mood = pressedData(step, '[data-rf="mood"] .rf-chip', 'mood')[0];
    }
    if (key === 'palette') answers.colors = pressedData(step, '.rf-sw', 'color');
    if (key === 'feel') {
      answers.material = pressedData(step, '[data-rf="material"] .rf-chip', 'material')[0];
      answers.use = pressedData(step, '[data-rf="use"] .rf-chip', 'use')[0];
      // The floor never reaches the engine — it is recorded because it is the single
      // most useful fact a specialist can have when advising on contrast, and it
      // belongs in the emailed brief ("dark walnut floors" says more than three
      // swatch names). It lives on THIS step, not with the rug's palette.
      answers.floor = pressedData(step, '[data-rf="floor"] .rf-chip', 'floor')[0];
    }
    if (key === 'budget') {
      const v = step.querySelector('[data-rf="budgetMax"]').value;
      answers.budgetMax = v ? Number(v) : undefined;
      // Age + one-of-a-kind live on this step since the merge.
      answers.condition = pressedData(step, '[data-rf="condition"] .rf-chip', 'condition')[0];
      answers.ooakOnly = step.querySelector('[data-rf="ooakOnly"]').checked || undefined;
    }
  }

  // The quiz renders at a generous premium width (operator preference); only the
  // results widen further to the full editorial grid. Keep these CONSTANT so the
  // card never "widens after going back" — every quiz path uses QUIZ_W.
  const QUIZ_W = '1080px';
  const RESULTS_W = '1320px';

  function next() { if (i < steps.length - 1) { i++; renderStep(); } else { finish(); } }
  function back() { if (i > 0) { i--; renderStep(); } }
  // A deliberate reset wipes the remembered brief too (Start over = clean slate).
  function restart() { i = 0; for (const k in answers) delete answers[k]; clearBrief(); wrapTo(QUIZ_W); renderStep(); }
  // Return to the quiz from the results WITHOUT clearing answers (the step nodes are
  // persistent, so the user's selections survive). Land on the LAST quiz step.
  function editAnswers() { i = steps.length - 1; wrapTo(QUIZ_W); renderStep(); track('rf_edit_answers'); }

  // ── Resume: push a saved `answers` object back into the live answers + step nodes ──
  // The step nodes are PERSISTENT (they hold selections across Back/Next and live
  // detached in `steps`), so we can re-populate their controls directly even though
  // they aren't currently in the DOM — then jump straight to results via finish().
  function restoreControls(saved) {
    // 1) live answers object: replace contents wholesale with the saved brief.
    for (const k in answers) delete answers[k];
    Object.assign(answers, saved);
    // 2) reflect each answer back onto its step's controls.
    const stepByKey = (k) => steps[STEP_KEYS.indexOf(k)];
    const pressMatch = (scope, sel, dataKey, values) => {
      const want = new Set((values || []).map(String));
      scope.querySelectorAll(sel).forEach((n) => n.setAttribute('aria-pressed', String(want.has(String(n.dataset[dataKey])))));
    };
    const roomStep = stepByKey('room');
    if (roomStep) pressMatch(roomStep, '.rf-chip', 'room', saved.room ? [saved.room] : []);
    const sizeStep = stepByKey('size');
    if (sizeStep) {
      // Clear any quick-size chip selection; the ft+in range is the source of truth.
      sizeStep.querySelectorAll('.rf-chip[data-size]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      RANGE_FIELDS.forEach((f) => setRange(sizeStep, f, typeof saved[f] === 'number' ? saved[f] : ''));
    }
    const styleStep = stepByKey('style');
    if (styleStep) {
      pressMatch(styleStep, '[data-rf="style"] .rf-chip', 'style', saved.styles || []);
      pressMatch(styleStep, '[data-rf="pattern"] .rf-chip', 'pattern', saved.pattern ? [saved.pattern] : []);
      pressMatch(styleStep, '[data-rf="mood"] .rf-chip', 'mood', saved.mood ? [saved.mood] : []);
    }
    const paletteStep = stepByKey('palette');
    if (paletteStep) pressMatch(paletteStep, '.rf-sw', 'color', saved.colors || []);
    const feelStep = stepByKey('feel');
    if (feelStep) {
      pressMatch(feelStep, '[data-rf="material"] .rf-chip', 'material', saved.material ? [saved.material] : []);
      pressMatch(feelStep, '[data-rf="use"] .rf-chip', 'use', saved.use ? [saved.use] : []);
      pressMatch(feelStep, '[data-rf="floor"] .rf-chip', 'floor', saved.floor ? [saved.floor] : []);
    }
    const budgetStep = stepByKey('budget');
    if (budgetStep) {
      const el = budgetStep.querySelector('[data-rf="budgetMax"]');
      if (el) el.value = (typeof saved.budgetMax === 'number' ? saved.budgetMax : '');
      // ⛔ stepByKey('character') would return undefined since the merge — restoring
      // from it fails silently, which is the worst kind of failure for a resume.
      pressMatch(budgetStep, '[data-rf="condition"] .rf-chip', 'condition', saved.condition ? [saved.condition] : []);
      const ooak = budgetStep.querySelector('[data-rf="ooakOnly"]'); if (ooak) ooak.checked = !!saved.ooakOnly;
    }
  }
  function resumeBrief() {
    const saved = readBrief();
    if (!saved) return;
    restoreControls(saved);
    track('rf_resume', {});
    finish(); // straight to the curated edit — the nicer "resume"
  }

  // The resume banner sits ABOVE the first step inside the stage. Shown only when a
  // non-empty brief is remembered; reuses the .rf-link token so it reads as the tool's
  // own quiet invitation, not a popup.
  // Styled inline with the theme's design tokens (keeps the whole feature inside this
  // one asset — no extra section CSS). flex-wrap + a flexible label keep it from
  // overflowing at 360px; the × stays a fixed tap target on its own.
  function renderResumeBanner() {
    if (!readBrief()) return;
    const bar = document.createElement('div');
    bar.setAttribute('data-rf-resume-bar', '');
    bar.style.cssText = 'display:flex;align-items:center;flex-wrap:wrap;gap:.6rem 1rem;' +
      'margin:0 0 1rem;padding:.85rem 1.1rem;background:var(--paper-deep);' +
      'border:1px solid var(--line);border-radius:var(--r-pill);box-shadow:var(--shadow-inset);';
    const txt = document.createElement('span');
    txt.textContent = 'Pick up where you left off?';
    txt.style.cssText = 'flex:1 1 auto;min-width:0;font-family:var(--font-serif);font-size:.92rem;color:var(--ink-soft);';
    const go = document.createElement('button');
    go.type = 'button';
    go.className = 'rf-link';
    go.setAttribute('data-rf-resume', '');
    go.innerHTML = 'Resume your last edit <span aria-hidden="true">&#8594;</span>';
    const x = document.createElement('button');
    x.type = 'button';
    x.setAttribute('data-rf-resume-dismiss', '');
    x.setAttribute('aria-label', 'Dismiss');
    x.innerHTML = '&times;';
    x.style.cssText = 'flex:0 0 auto;width:28px;height:28px;display:grid;place-items:center;' +
      'border:1px solid var(--line);background:var(--paper);border-radius:50%;font-size:1.05rem;' +
      'line-height:1;color:var(--ink-soft);cursor:pointer;';
    bar.append(txt, go, x);
    stage.insertBefore(bar, stage.firstChild);
  }

  // ── answers → engine FormSlot ──────────────────────────────────────────────
  // Size now flows from the ft+in RANGE answers (set by a quick-size chip OR typed by
  // hand) — passed straight into the slot; the bundled formBriefToDesignBrief converts
  // ft+in → inches (same as the Trade brief). shape comes from the runner/round chips.
  function answersToSlot() {
    const slot = {};
    if (answers.styles && answers.styles.length) slot.styles = answers.styles;
    if (answers.colors && answers.colors.length) slot.colors = answers.colors;
    if (answers.material) slot.mustHaves = [answers.material];
    if (answers.condition) slot.condition = answers.condition;
    if (answers.ooakOnly) slot.ooakOnly = true;
    if (typeof answers.budgetMax === 'number') slot.budgetMax = answers.budgetMax;
    // "busy" (kids & pets) nudges toward durable wool — only if no material chosen.
    if (answers.use === 'busy' && !answers.material) slot.mustHaves = ['Wool'];
    if (answers.shape) slot.shape = answers.shape;
    RANGE_FIELDS.forEach((f) => { if (typeof answers[f] === 'number') slot[f] = answers[f]; });
    return slot;
  }

  // Compact size string for telemetry only (e.g. 5'0"–5'11" × 8'0"–8'11"). "" when blank.
  function sizeSummary() {
    const ft = (f, i) => (typeof answers[f] === 'number' ? answers[f] + "'" + (answers[i] || 0) + '"' : '');
    const band = (minF, minI, maxF, maxI) => {
      const lo = ft(minF, minI), hi = ft(maxF, maxI);
      return lo || hi ? (lo + (hi ? '–' + hi : '+')) : '';
    };
    const w = band('widthMinFt', 'widthMinIn', 'widthMaxFt', 'widthMaxIn');
    const l = band('lengthMinFt', 'lengthMinIn', 'lengthMaxFt', 'lengthMaxIn');
    if (!w && !l) return '';
    return [w, l].filter(Boolean).join(' × ');
  }

  // Templated (no-AI) narrative line that reflects the brief back.
  function narrative() {
    const room = answers.room ? answers.room.toLowerCase() : 'your space';
    const style = (answers.styles && answers.styles[0]) ? answers.styles[0].toLowerCase() + ' ' : '';
    const palette = (answers.colors && answers.colors.length) ? ` in the ${answers.colors.slice(0, 2).join(' & ').toLowerCase()} you chose` : '';
    return `A hand-picked edit of ${style}pieces for ${room}${palette} — every one a real, one-of-a-kind rug, in stock now.`;
  }

  // The feed gives shopify_product_id as a GID (gid://shopify/Product/123…), but the
  // site-wide favourites system keys off the BARE numeric id ({{ p.id }} in
  // snippets/product-card.liquid → localStorage 'arteverk_favs'). Strip the GID prefix
  // so a rug favourited from the Finder lights up its heart on every other surface
  // (collection cards, PDP, related, nav count) — one shared wishlist, not a twin.
  const favId = (gid) => String(gid == null ? '' : gid).replace(/^gid:\/\/shopify\/Product\//, '');

  // ── The site product card, rebuilt in JS to mirror snippets/product-card.liquid.
  // RETAIL price only (rug.price_cents) — NO tradePriceCents, no "trade" tag. The
  // favourites heart IS the site's own (.card-fav + data-fav-id/data-fav-name), wired
  // to the global wishlist below. Same classes/DOM so global theme.css styles it
  // identically; p.reasons surfaces a subtle "why this fits" line (curated touch).
  function cardHTML(p) {
    const rug = p.rug;
    const name = rug.rug_name || rug.title || '';
    const hasName = !!(rug.rug_name && rug.title && rug.rug_name !== rug.title);
    const eyebrow = (rug.styles && rug.styles.length ? rug.styles[0] : rug.origin) || '';
    const url = rug.product_url || (rug.handle ? '/products/' + rug.handle : '#');
    const img = rug.image_url
      ? `<img class="base" src="${esc(imgAt(rug.image_url, 600))}" srcset="${esc(imgAt(rug.image_url, 600))} 600w, ${esc(imgAt(rug.image_url, 900))} 900w" sizes="(max-width:520px) 92vw, (max-width:880px) 45vw, 30vw" alt="${esc(name)}" loading="lazy" width="600" height="750">`
      : '';
    let badges = '';
    if (rug.is_ooak) badges += `<span class="badge ooak"><span class="mk" aria-hidden="true">&#9670;</span>1 of 1</span>`;
    if (rug.masterpiece) badges += `<span class="badge master"><span class="mk" aria-hidden="true">&#9670;</span>Masterpiece</span>`;
    const price = rug.price_cents != null ? money(rug.price_cents) : 'Price on request';
    const fid = favId(rug.shopify_product_id);
    // .card-fav is a SIBLING of the card-wide <a> (an interactive control must never
    // nest inside an anchor — matches product-card.liquid exactly). theme.js finds it
    // by [data-fav-id]; we re-bind dynamically below since its handler binds on load.
    const fav = fid
      ? `<button class="card-fav" type="button" data-fav-id="${esc(fid)}" data-fav-name="${esc(name)}" aria-pressed="false" aria-label="Add ${esc(name)} to your favorites"><span class="ch" aria-hidden="true">&#9825;</span></button>`
      : '';
    const why = (p.reasons || []).slice(0, 2).join(' · ');
    return `<article class="card reveal in">
      ${fav}
      <a class="card-core" href="${esc(url)}" aria-label="${esc(name)}${hasName ? ' — ' + esc(rug.title) : ''}">
        <div class="card-img">${badges}${img}</div>
        <div class="card-body">
          ${eyebrow ? `<div class="card-collection">${esc(eyebrow)}</div>` : ''}
          <h3 class="card-name">${esc(name)}</h3>
          ${hasName ? `<div class="card-descriptor">${esc(rug.title)}</div>` : ''}
          <div class="card-foot"><div class="card-price">${price}</div></div>
          ${why ? `<div class="card-why">${esc(why)}</div>` : ''}
        </div>
      </a>
    </article>`;
  }

  // ── Site-wide favourites, wired to dynamically-rendered Finder cards ──────────
  // theme.js's favourites handler BINDS ON LOAD (it iterates [data-fav-id] once at
  // init and marks each btn._wired), so cards the Finder injects LATER are never wired
  // by it. We add our own delegated handler here that mutates the SAME localStorage
  // key + the SAME nav count + the SAME .saved class theme.js uses — so the two are one
  // wishlist. (theme.js's own re-wire helpers aren't exposed, hence the delegated path.)
  const FAV_KEY = 'arteverk_favs';
  const readFavs = () => { try { return JSON.parse(localStorage.getItem(FAV_KEY)) || []; } catch (_) { return []; } };
  const writeFavs = (arr) => { try { localStorage.setItem(FAV_KEY, JSON.stringify(arr)); } catch (_) {} };
  const navFav = document.querySelector('[data-fav-nav]');
  const navCount = document.querySelector('[data-fav-count]');
  function paintFavCount() {
    const n = readFavs().length;
    if (navCount) { navCount.textContent = n; navCount.hidden = n === 0; }
    if (navFav) navFav.classList.toggle('has', n > 0);
  }
  // Reflect saved state on render so a rug already in favs shows the FILLED heart.
  function paintFavBtn(btn) {
    const saved = readFavs().indexOf(btn.dataset.favId) !== -1;
    btn.classList.toggle('saved', saved);
    btn.setAttribute('aria-pressed', String(saved));
  }
  function paintFinderFavs() { stage.querySelectorAll('.rf-results .card-fav[data-fav-id]').forEach(paintFavBtn); }

  // ── No dead-ends: smart auto-relax ──────────────────────────────────────────
  // When the brief matches a thin set, progressively LOOSEN it (clone-per-step, never
  // mutating the caller's brief) and re-match, stopping the moment we clear the floor —
  // so the visitor ALWAYS sees a real, good edit instead of an empty state. Returns the
  // last (most-relaxed) result set tried plus a human note naming what we opened up.
  const RELAX_FLOOR = 6;
  const SIZE_FIELDS = ['widthMinIn', 'widthMaxIn', 'lengthMinIn', 'lengthMaxIn'];
  function hasSize(b) { return SIZE_FIELDS.some((f) => typeof b[f] === 'number'); }
  function matchWithRelax(feed, brief) {
    let results;
    try { results = M.matchRugs(feed, brief); } catch (_) { results = []; }
    if (results.length >= RELAX_FLOOR) return { results, note: '' };

    // Each relaxation: a label (for the note) + a guard + a transform on a CLONE.
    const steps = [
      { tag: 'size', when: (b) => hasSize(b), apply: (b) => {
          // Widen the size window by a foot each way (floor mins at 0).
          if (typeof b.widthMinIn === 'number') b.widthMinIn = Math.max(0, b.widthMinIn - 12);
          if (typeof b.widthMaxIn === 'number') b.widthMaxIn += 12;
          if (typeof b.lengthMinIn === 'number') b.lengthMinIn = Math.max(0, b.lengthMinIn - 12);
          if (typeof b.lengthMaxIn === 'number') b.lengthMaxIn += 12;
        } },
      { tag: 'size', when: (b) => hasSize(b), apply: (b) => { SIZE_FIELDS.forEach((f) => delete b[f]); } },
      { tag: 'one-of-a-kind', when: (b) => b.ooakOnly, apply: (b) => { delete b.ooakOnly; } },
      { tag: 'budget', when: (b) => typeof b.budgetMaxCents === 'number', apply: (b) => { delete b.budgetMaxCents; } },
    ];

    let cur = Object.assign({}, brief);
    const relaxed = [];
    for (const s of steps) {
      if (!s.when(cur)) continue;
      cur = Object.assign({}, cur); // fresh clone before each mutation
      s.apply(cur);
      if (!relaxed.includes(s.tag)) relaxed.push(s.tag);
      try { results = M.matchRugs(feed, cur); } catch (_) { results = []; }
      if (results.length >= RELAX_FLOOR) break;
    }
    return { results, note: relaxNote(relaxed) };
  }
  // Map the applied relaxations to a natural, honest sentence.
  function relaxNote(relaxed) {
    if (!relaxed.length) return '';
    const label = { size: 'the size', budget: 'your budget', 'one-of-a-kind': 'the one-of-a-kind filter' };
    const parts = relaxed.map((t) => label[t]).filter(Boolean);
    let joined;
    if (parts.length === 1) joined = parts[0];
    else if (parts.length === 2) joined = parts[0] + ' and ' + parts[1];
    else joined = parts.slice(0, -1).join(', ') + ', and ' + parts[parts.length - 1];
    return `To show you more, we opened up ${joined}.`;
  }

  // ── Match + render ─────────────────────────────────────────────────────────
  let ranked = [];
  let cursor = 0;
  function finish() {
    if (!M || !M.formBriefToDesignBrief || !M.matchRugs) {
      stage.innerHTML = '<p class="rf-empty">The finder couldn\'t load — please refresh and try again.</p>';
      return;
    }
    wrapTo(RESULTS_W); // results want the full editorial grid width
    // Tasteful interim state so there's no blank flash while the feed/match resolve.
    // Width is already set (wrapTo above), so the loading card sits at the results width.
    stage.innerHTML = `
      <div class="rf-loading">
        <p class="rf-eyebrow">Your edit</p>
        <h2 class="rf-title editorial">Curating your edit&hellip;</h2>
        <p class="rf-narrative">Hand-picking pieces that fit&hellip;</p>
      </div>`;
    // count:60 must go on the BRIEF, not the slot — formBriefToDesignBrief hardcodes
    // count:12 and ignores any count on the FormSlot. Without this the matcher only ever
    // returns 12, so the "Show me 12 more" button could never appear. (Mirrors Trade.)
    // ⛔ THIS CALL THROWS ON AN EMPTY BRIEF ("a slot needs at least one of
    // style/colour/shape/size/budget") and it used to be unwrapped — so a visitor who
    // answered only the room and clicked through sat on "Curating your edit…" FOREVER.
    // Reproduced live 2026-08-18. The irony is that matchWithRelax below exists
    // precisely so there are no dead ends; the dead end was one line above it.
    // A bare { count } brief is valid for the MATCHER (only the mapper is strict), so
    // the fallback still returns real rugs — ranked on caliber — rather than nothing.
    let brief;
    try {
      brief = Object.assign(M.formBriefToDesignBrief(answersToSlot()), { count: 60 });
    } catch (_) {
      brief = { count: 60 };
      track('rf_empty_brief', {});
    }
    saveBrief(); // remember this brief so a returning visitor can resume it
    track('rf_edit_built', {
      styles: (answers.styles || []).join('|'),
      // Compact size summary from the range (e.g. "5'0\"-5'11\" x 8'0\"-8'11\"") + shape.
      size: sizeSummary(),
      shape: answers.shape || '',
      colors: (answers.colors || []).join('|'),
    });
    loadFeed().then((feed) => {
      // No dead-ends: auto-relax a thin brief so the visitor always gets a real edit.
      const relaxed = matchWithRelax(feed, brief);
      ranked = relaxed.results || [];
      const relaxNoteText = relaxed.note || '';
      cursor = 0;
      stage.innerHTML = `
        <div class="rf-results">
          <div class="rf-results-top">
            <p class="rf-eyebrow">Your edit</p>
            <button type="button" class="rf-link rf-editlink" data-rf-edit><span aria-hidden="true">&#8592;</span> Edit my answers</button>
          </div>
          <h2 class="rf-title editorial">Curated for you</h2>
          <p class="rf-narrative">${esc(narrative())}</p>
          ${relaxNoteText ? `<p class="rf-relax-note">${esc(relaxNoteText)}</p>` : ''}
          <div class="cards" data-rf-grid></div>
          <div class="rf-more" data-rf-more></div>
          <div class="rf-capture" data-rf-capture></div>
          <button type="button" class="rf-restart" data-rf-restart>Start over</button>
        </div>`;
      if (ranked.length) {
        renderBatch();
      } else {
        // Now-rare genuinely-empty case (even fully relaxed found nothing).
        stage.querySelector('[data-rf-grid]').innerHTML =
          '<p class="rf-empty">No in-stock match yet — <button type="button" class="rf-link" data-rf-restart>start over</button> and widen the brief (loosen size or drop a color).</p>';
      }
      track('rf_edit_shown', {
        count: ranked.length,
        relaxed: relaxNoteText ? 1 : 0,
        ids: ranked.slice(0, 12).map((p) => p.rug.shopify_product_id).join(','),
      });
      renderCapture();
      // The quiz has just been replaced by the results — bring them into view so the
      // user isn't left staring at the How-it-works intro above the tool.
      root.querySelector('.rf-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function renderBatch() {
    const batch = ranked.slice(cursor, cursor + 12);
    cursor += batch.length;
    const grid = stage.querySelector('[data-rf-grid]');
    if (grid) grid.insertAdjacentHTML('beforeend', batch.map(cardHTML).join(''));
    paintFinderFavs(); // reflect already-saved rugs as filled hearts on the new cards
    const more = stage.querySelector('[data-rf-more]');
    if (more) more.innerHTML = cursor < ranked.length
      ? '<button type="button" class="rf-link" data-rf-fresh>Show me 12 more <span aria-hidden="true">&#8594;</span></button>'
      : '';
  }

  // ── Soft capture row (refine chips + email-my-edit + specialist/viewing) ─────
  function renderCapture() {
    const el = stage.querySelector('[data-rf-capture]');
    if (!el) return;
    el.innerHTML = `
      <div class="rf-refine"><span class="rf-refine-l">Refine:</span>
        <button type="button" class="rf-chip rf-mini" data-refine="size">Loosen size</button>
        <button type="button" class="rf-chip rf-mini" data-refine="palette">Change colors</button>
        <button type="button" class="rf-chip rf-mini" data-refine="budget">Adjust budget</button>
      </div>
      <div class="rf-soft">
        <form class="rf-email" data-rf-email>
          <input type="email" name="email" placeholder="Email me this edit" required>
          <button type="submit" class="tcb-btn">Send it</button>
          <label class="rf-optin">
            <input type="checkbox" name="rf_optin" data-rf-optin>
            <span>Also email me new arrivals and offers &mdash; including 10% off your first rug.</span>
          </label>
        </form>
        <a class="rf-link" href="/pages/contact-us">Talk to a specialist</a>
        <a class="rf-link" href="/pages/visit-us#book">Book a viewing</a>
      </div>`;
  }

  // ── ONE delegated click handler. Results actions are checked first (and return)
  // so a refine mini-chip (which also carries .rf-chip) never falls through to the
  // quiz chip toggle. Quiz + results targets are otherwise disjoint. ─────────────
  stage.addEventListener('click', (e) => {
    // Resume banner (sits above the first step) — checked first.
    if (e.target.closest('[data-rf-resume]')) { resumeBrief(); return; }
    if (e.target.closest('[data-rf-resume-dismiss]')) {
      // Hide only — keep the saved brief so it's still offered next visit.
      const bar = e.target.closest('[data-rf-resume-bar]'); if (bar) bar.remove();
      track('rf_resume_dismiss', {});
      return;
    }
    // Results-area actions (checked first so they never fall through to a quiz chip).
    if (e.target.closest('[data-rf-edit]')) { editAnswers(); return; }
    if (e.target.closest('[data-rf-fresh]')) { renderBatch(); track('rf_fresh_options'); return; }
    if (e.target.closest('[data-rf-restart]')) { restart(); return; }
    const refine = e.target.closest('[data-refine]');
    if (refine) {
      const k = refine.dataset.refine;
      i = STEP_KEYS.indexOf(k); if (i < 0) i = 0;
      wrapTo(QUIZ_W);
      renderStep();
      track('rf_refine', { what: k });
      return;
    }
    // Favourites heart on a result card → toggle the SITE-WIDE wishlist (same key,
    // same nav count, same .saved class theme.js uses). Checked before .card-core so a
    // tap on the heart favourites instead of navigating.
    const favBtn = e.target.closest('.card-fav[data-fav-id]');
    if (favBtn) {
      e.preventDefault();
      e.stopPropagation();
      const favs = readFavs();
      const id = favBtn.dataset.favId;
      const nowSaved = favs.indexOf(id) === -1;
      writeFavs(nowSaved ? favs.concat([id]) : favs.filter((x) => x !== id));
      paintFavBtn(favBtn);
      paintFavCount();
      if (nowSaved) {
        track('add_to_wishlist', { items: [{ item_id: id, item_name: favBtn.dataset.favName || '' }] });
        favBtn.classList.add('bump');
        setTimeout(() => favBtn.classList.remove('bump'), 520);
        if (navFav) { navFav.classList.add('bump'); setTimeout(() => navFav.classList.remove('bump'), 520); }
      }
      return;
    }
    if (e.target.closest('.card-core')) { track('rf_card_click'); return; } // let the link navigate

    // Quiz actions.
    // Skip an OPTIONAL step (advances with no selection) — same path as Next.
    if (e.target.closest('[data-rf-skip]')) { track('rf_skip', { step: STEP_KEYS[i] }); collectStep(); next(); return; }
    if (e.target.closest('[data-rf-branch]')) { track('rf_designer_branch'); location.href = root.dataset.tradeUrl; return; }
    if (e.target.closest('[data-rf-sizeguide]')) { e.preventDefault(); window.open(root.dataset.sizeGuide, '_blank'); return; }

    // Size quick-picks: single-select among siblings + FILL the ft+in range (mirrors
    // the Trade brief). Tapping the active chip clears the chip + the ranges.
    const sizeChip = e.target.closest('.rf-chip[data-size]');
    if (sizeChip) {
      const step = sizeChip.closest('.rf-step');
      const wasOn = sizeChip.getAttribute('aria-pressed') === 'true';
      step.querySelectorAll('.rf-chip[data-size]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      if (wasOn) { clearRanges(step); delete answers.shape; return; }
      sizeChip.setAttribute('aria-pressed', 'true');
      applySize(step, sizeChip.dataset.size);
      return;
    }
    const chip = e.target.closest('.rf-chip:not([data-size])');
    if (chip) {
      const group = chip.closest('[data-rf]');
      if (!group) return; // not a quiz chip (e.g. stray) — ignore
      const multi = group.dataset.rf === 'style';
      // Same honesty rule as the palette groups: touching a tradition tile by hand
      // clears the pattern chip, which would otherwise claim a grouping that no
      // longer holds.
      if (group.dataset.rf === 'style') {
        const st = chip.closest('.rf-step');
        if (st) st.querySelectorAll('[data-rf="pattern"] .rf-chip, [data-rf="mood"] .rf-chip')
          .forEach((c) => c.setAttribute('aria-pressed', 'false'));
      }
      if (!multi) group.querySelectorAll('.rf-chip').forEach((c) => { if (c !== chip) c.setAttribute('aria-pressed', 'false'); });
      chip.setAttribute('aria-pressed', chip.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      // ⛔ THE FLOOR NO LONGER TOUCHES THE RUG'S COLOURS (operator, 2026-08-17).
      // The first version pressed swatches when a floor was chosen, which quietly
      // conflated two different statements: "my floor is dark walnut" is not "I want
      // a red rug". The floor is now recorded and nothing else.
      if (group.dataset.rf === 'floor' && chip.getAttribute('aria-pressed') === 'true') {
        track('rf_floor', { floor: chip.dataset.floor });
      }
      // A colour PALETTE is a shortcut into the swatches — the dots remain the single
      // source of truth for answers.colors, so the engine contract is unchanged.
      // Mood NUDGES: it fills the tradition tiles only when none are chosen, so an
      // explicit choice always wins. It never touches the palette step — a control
      // silently changing a screen the visitor is not looking at is not a nudge.
      if (group.dataset.rf === 'mood' && chip.getAttribute('aria-pressed') === 'true') {
        const step = chip.closest('.rf-step');
        const already = step ? step.querySelectorAll('[data-rf="style"] .rf-chip[aria-pressed="true"]').length : 1;
        const want = new Set((chip.dataset.styles || '').split('|').filter(Boolean));
        if (step && !already && want.size) {
          step.querySelectorAll('[data-rf="style"] .rf-chip').forEach((t) => {
            t.setAttribute('aria-pressed', String(want.has(t.dataset.style)));
          });
        }
        track('rf_mood', { mood: chip.dataset.mood });
      }
      // Pattern layout -> the tradition tiles that ARE that layout. Descriptive, not a
      // taste pairing (see the section comment), so it is safe to write `styles`.
      if (group.dataset.rf === 'pattern' && chip.getAttribute('aria-pressed') === 'true') {
        const step = chip.closest('.rf-step');
        const want = new Set((chip.dataset.styles || '').split('|').filter(Boolean));
        if (step && want.size) {
          step.querySelectorAll('[data-rf="style"] .rf-chip').forEach((t) => {
            t.setAttribute('aria-pressed', String(want.has(t.dataset.style)));
          });
          track('rf_pattern', { pattern: chip.dataset.pattern });
        }
      }
      if (group.dataset.rf === 'palette-group' && chip.getAttribute('aria-pressed') === 'true') {
        const step = chip.closest('.rf-step');
        const want = new Set((chip.dataset.colors || '').split('|').filter(Boolean));
        if (step && want.size) {
          step.querySelectorAll('.rf-sw').forEach((sw) => {
            sw.setAttribute('aria-pressed', String(want.has(sw.dataset.color)));
          });
          track('rf_palette', { palette: chip.dataset.palette });
        }
      }
      return;
    }
    const sw = e.target.closest('.rf-sw');
    if (sw) {
      sw.setAttribute('aria-pressed', sw.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
      // Hand-editing a dot clears the palette highlight — leaving it lit would claim a
      // grouping the swatches no longer match.
      const step = sw.closest('.rf-step');
      if (step) step.querySelectorAll('[data-rf="palette-group"] .rf-chip')
        .forEach((c) => c.setAttribute('aria-pressed', 'false'));
      return;
    }
    if (e.target.closest('[data-rf-next]')) { collectStep(); next(); return; }
    if (e.target.closest('[data-rf-back]')) { back(); return; }
  });

  // Editing a size range by hand de-selects the quick-pick chip (and any runner/round
  // shape it implied) — a typed custom range is then the source of truth.
  stage.addEventListener('input', (e) => {
    if (e.target.matches('.rf-range input[type="number"]')) {
      const step = e.target.closest('.rf-step');
      step.querySelectorAll('.rf-chip[data-size][aria-pressed="true"]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      delete answers.shape;
    }
  });

  // Email capture → native Shopify contact form (→ inbox / Klaviyo). Non-blocking.
  stage.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-rf-email]');
    if (!form) return;
    e.preventDefault();
    const email = form.querySelector('input[name=email]').value.trim();
    if (!email) return;
    const body = new URLSearchParams();
    body.set('form_type', 'contact'); body.set('utf8', '✓');
    body.set('contact[email]', email);
    body.set('contact[subject]', 'Rug Finder edit');
    body.set('contact[body]', JSON.stringify({
      kind: 'rug_finder_lead',
      answers,
      ids: ranked.slice(0, 12).map((p) => p.rug.shopify_product_id),
    }));
    fetch(root.dataset.contact, { method: 'POST', body }).catch(() => {});

    /*
     * ⭑ AND INTO THE ERP, WHERE SOMEBODY CAN ACTUALLY ANSWER IT.
     *
     * ⛔ THE POST ABOVE REACHES NOBODY WHO WORKS HERE. It is Shopify's native contact form: it emails
     * a JSON blob to the store address and stops. Nothing parses it, no CRM record is created, and
     * `rug_finder` has had ZERO leads on production while this quiz has been collecting them — so a
     * person who told us their room, their size, their palette and their address was, to the people
     * who could have sold them a rug, invisible.
     *
     * ⭑ /apps/trade/enquire IS OUR OWN RAIL. The type is declared, so the lead arrives typed
     * `rug_finder` rather than inferred from a customer tag, and the brief travels with it as prose a
     * human can read in the queue — the same brief the Klaviyo edit email uses.
     *
     * ⛔ ADDITIVE, NEVER A REPLACEMENT. The contact post, the Klaviyo subscription and the GA4
     * `generate_lead` below all stay exactly as they are: this quiz already earns its keep, and a
     * rewrite that improved the CRM by breaking the measurement chain would be a bad trade. Same
     * fire-and-forget shape, so nothing the customer sees waits on it.
     *
     * ⛔ CONSENT IS FORWARDED, NOT ASSUMED. Asking us to email an edit is a service request; the
     * SEPARATE tickbox is the permission. The ERP is told which of the two happened, and defaults to
     * the safer answer when the box is absent.
     */
    try {
      var erp = new FormData();
      erp.set('type', 'finder');
      erp.set('name', email.split('@')[0] || 'Rug finder');
      erp.set('email', email);
      erp.set('detail', [
        'Rug Finder edit — ' + ranked.length + ' matches.',
        'Room: ' + (answers.room || '—'),
        'Size: ' + (sizeSummary() || '—'),
        'Shape: ' + (answers.shape || '—'),
        'Mood: ' + (answers.mood || '—'),
        'Pattern: ' + (answers.pattern || '—'),
        'Styles: ' + ((answers.styles || []).join(', ') || '—'),
        'Top picks: ' + ranked.slice(0, 12).map(function (p) { return p.rug.shopify_product_id; }).join(', ')
      ].join('\n'));
      /* ⛔ READ HERE, NOT FROM `optin` BELOW. That variable is declared further down and `var` hoists
         it, so at this point it is `undefined` — forwarding consent from it would have silently sent
         "no" for every person who ticked the box. */
      var erpOptin = form.querySelector('[data-rf-optin]');
      if (erpOptin && erpOptin.checked) erp.set('consent_email', 'yes');
      fetch('/apps/trade/enquire?type=finder', { method: 'POST', body: erp, keepalive: true })
        .catch(function () {});
    } catch (err) { /* the edit is what they asked for; never let the CRM hop break it */ }

    /*
     * MARKETING CONSENT IS A SEPARATE ANSWER FROM THE SERVICE REQUEST.
     *
     * Asking us to email an edit is a relationship, not permission to market —
     * that distinction is the house consent rule, and it is why captureLead()
     * only ever adds these people to a list WITHOUT consent, leaving them
     * unmailable. Rather than force consent on their behalf, we ask.
     *
     * Ticked  -> Klaviyo CLIENT subscription (public company id, real recorded
     *            consent), which is also what triggers the welcome flow.
     * Unticked-> nothing here. They still get their edit, and they still reach
     *            the CRM and the ERP through the normal forms-ingest path.
     */
    var subscribePromise = null;
    var optin = form.querySelector('[data-rf-optin]');
    if (optin && optin.checked) {
      /*
       * AWAITED ON PURPOSE — and the ordering below depends on it.
       *
       * The "Your Edit" flow triggers on the `Rug Finder Edit` metric but carries a
       * profile filter requiring email marketing consent (Klaviyo will not send a flow
       * email without it). This subscribe used to be fire-and-forget, so the metric was
       * pushed ~1s BEFORE consent was recorded: at trigger evaluation the profile was
       * not yet subscribed, "Your Edit" filtered the person out, and the only thing they
       * received was the generic newsletter welcome. Someone who asked for their rugs got
       * a brand email instead. Measured on a real submission: metric 12:18:43, consent
       * 12:18:44, welcome sent 12:19:17, edit never sent.
       *
       * So: record consent, THEN fire the metric.
       */
      subscribePromise = fetch('https://a.klaviyo.com/client/subscriptions/?company_id=VpAbmY', {
        method: 'POST',
        keepalive: true,
        headers: { 'content-type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({ data: { type: 'subscription', attributes: {
          custom_source: 'Rug Finder edit',
          profile: { data: { type: 'profile', attributes: { email: email } } }
        }, relationships: { list: { data: { type: 'list', id: 'Sncbp4' } } } } })
      }).catch(function () {});
      track('marketing_optin', { method: 'rug_finder' });
    }
    /*
     * GA4 `generate_lead` — the event Google Ads' "Submit lead form" conversion
     * is built from (it is a GA4 IMPORT, type 40, and biddable). Without this
     * the quiz captured the email, mailed the edit and told Google nothing, so
     * the account's one biddable lead goal read 0 for 90 days and Smart Bidding
     * had no signal to optimise toward. Fired only AFTER a real address is in
     * hand — never on step view, never on render.
     */
    track('generate_lead', { method: 'rug_finder', currency: 'USD', value: 0 });
    // Klaviyo: identify the lead + fire the "Rug Finder Edit" event so the "your edit" flow can send.
    // Deliberately deferred until the subscription above has settled — see the note there.
    function pushEdit() {
    try {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push(['identify', { '$email': email }]);
      window.klaviyo.push(['track', 'Rug Finder Edit', {
        Count: ranked.length,
        // ⭐ The BRIEF travels with the edit. Without it the "Your Edit" email can show
        // the rugs but cannot say why they are these rugs — and "for your dining room,
        // in earth & spice, on natural wood" is the whole difference between a curated
        // edit and a product grid. It also puts the floor in front of a human, which is
        // the only reason we ask for it (it deliberately does not filter results).
        // Preference data only; the address is already on the identify call above.
        Brief: {
          Room: answers.room || '',
          Size: sizeSummary(),
          Shape: answers.shape || '',
          Mood: answers.mood || '',
          Pattern: answers.pattern || '',
          Styles: (answers.styles || []).join(', '),
          Colors: (answers.colors || []).join(', '),
          Floor: answers.floor || '',
          Material: answers.material || '',
          Age: answers.condition || '',
          OneOfAKindOnly: !!answers.ooakOnly,
          BudgetMax: typeof answers.budgetMax === 'number' ? answers.budgetMax : ''
        },
        Rugs: ranked.slice(0, 8).map(function (p) {
          var r = p.rug;
          return {
            ProductID: String(r.shopify_product_id).split('/').pop(),
            ProductName: r.rug_name || r.title || '',
            ImageURL: r.image_url ? (r.image_url + (r.image_url.indexOf('?') > -1 ? '&' : '?') + 'width=400') : '',
            Price: r.price_cents != null ? (r.price_cents / 100) : null,
            URL: r.product_url || (r.handle ? '/products/' + r.handle : '')
          };
        })
      }]);
    } catch (e) { /* non-blocking */ }
    }
    if (subscribePromise) { subscribePromise.then(pushEdit, pushEdit); } else { pushEdit(); }
    form.outerHTML = '<p class="rf-sent">On its way — check your inbox. ✦</p>';
    track('rf_lead', {});
  });

  // ── Prefill from the welcome popup ───────────────────────────────────────────
  // The front-door popup asks the room question before asking for an email.
  // Consuming that answer here is the whole point: the visitor is never asked
  // the same question twice, which is exactly what a third-party quiz form
  // bolted onto a storefront cannot do.
  //
  // ⛔ DELIBERATELY A DIFFERENT KEY FROM RESUME_KEY. `rf_last_brief` means "a
  // FINISHED brief — restore it and jump straight to results" (resumeBrief()
  // calls finish()). A room-only answer is not a brief; routing it through that
  // path would drop the visitor onto an "edit" computed from a single answer.
  const PREFILL_KEY = 'rf_prefill';
  function applyPrefill() {
    // ⭐ THE URL IS CHECKED FIRST, AND IT IS THE RELIABLE CHANNEL.
    // Measured in an in-app browser with localStorage writes refused: the stored
    // handoff simply never existed, so the visitor was asked their room twice.
    // A query parameter cannot be refused by a webview, so it leads; storage
    // remains as the backup for any path that does not carry one.
    let pre = null;
    try {
      const fromUrl = new URLSearchParams(location.search).get('room');
      if (fromUrl) pre = { room: fromUrl };
    } catch (_) {}
    if (!pre) {
      try { pre = JSON.parse(localStorage.getItem(PREFILL_KEY) || 'null'); } catch (_) {}
    }
    // Single use — and cleared even when unusable, so a malformed key cannot sit
    // there being retried on every future visit.
    try { localStorage.removeItem(PREFILL_KEY); } catch (_) {}
    // Strip the parameter once consumed: leaving it in means a shared or
    // bookmarked link silently re-answers a question for whoever opens it.
    try {
      if (new URLSearchParams(location.search).get('room') && window.history.replaceState) {
        const u = new URL(location.href);
        u.searchParams.delete('room');
        window.history.replaceState({}, '', u.toString());
      }
    } catch (_) {}
    if (!pre || !pre.room) return;

    const roomIdx = STEP_KEYS.indexOf('room');
    const roomStep = steps[roomIdx];
    if (!roomStep) return;

    // Only accept a room the quiz actually offers. An unrecognised value would
    // press nothing and then advance PAST a question with no answer recorded —
    // worse than not prefilling at all.
    const chip = [...roomStep.querySelectorAll('.rf-chip')]
      .find((n) => n.dataset.room === String(pre.room));
    if (!chip) return;

    roomStep.querySelectorAll('.rf-chip').forEach((n) => n.setAttribute('aria-pressed', 'false'));
    chip.setAttribute('aria-pressed', 'true');
    answers.room = pre.room;
    // The popup path SKIPS the room step, so collectStep('room') never runs — without
    // this the visitor who came through the front door would be the only one whose
    // room did not suggest a size, which is backwards.
    suggestSizeFromRoom();
    if (roomIdx < steps.length - 1) i = roomIdx + 1;
    track('rf_prefill_room', { room: pre.room });
  }
  applyPrefill();

  // ── Init — set the generous quiz width, then exactly one renderStep() ─────────
  wrapTo(QUIZ_W);
  renderStep();
  renderResumeBanner(); // offer "pick up where you left off" above the first step
})();
